import { motion, AnimatePresence } from "motion/react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { RIASEC_TYPES } from "../../Data/mockDataCareerTest";

const OPTION_STYLES = [
  {
    value: 2,
    label: "Yes, definitely!",
    color: "border-violet-300 bg-violet-50 text-violet-700",
    active: "border-violet-600 bg-violet-600 text-white",
  },
  {
    value: 1,
    label: "Somewhat",
    color: "border-slate-200  bg-slate-50  text-slate-700",
    active: "border-slate-700  bg-slate-700  text-white",
  },
  {
    value: 0,
    label: "Not really",
    color: "border-rose-200   bg-rose-50   text-rose-700",
    active: "border-rose-500   bg-rose-500   text-white",
  },
] as const;

export default function QuestionCard({
  question,
  index,
  total,
  answer,
  onAnswer,
  onNext,
  onBack,
  direction,
}: {
  question: (typeof RIASEC_QUESTIONS)[0];
  index: number;
  total: number;
  answer: 0 | 1 | 2 | null;
  onAnswer: (v: 0 | 1 | 2) => void;
  onNext: () => void;
  onBack: () => void;
  direction: "forward" | "back";
}) {
  const type = RIASEC_TYPES.find((t) => t.code === question.type)!;
  const enterX = direction === "forward" ? 40 : -40;
  const exitX = direction === "forward" ? -40 : 40;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={question.id}
        initial={{ opacity: 0, x: enterX }}
        animate={{ opacity: 0.99, x: 0 }}
        exit={{ opacity: 0, x: exitX }}
        transition={{ duration: 0.28, ease: "easeInOut" }}
        className="max-w-xl mx-auto"
      >
        {/* Type badge */}
        <div className="flex justify-center mb-5">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[12px]"
            style={{
              borderColor: `${type.color}40`,
              backgroundColor: `${type.color}12`,
              color: type.color,
              fontWeight: 700,
            }}
          >
            <span>{type.emoji}</span> {type.label}
          </div>
        </div>

        {/* Question */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 mb-5 text-center">
          <p
            className="text-[20px] lg:text-[24px] text-slate-900 leading-snug"
            style={{ fontWeight: 800 }}
          >
            {question.text}
          </p>
        </div>

        {/* Options */}
        <div className="flex flex-col gap-3 mb-8">
          {OPTION_STYLES.map((opt) => {
            const selected = answer === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => onAnswer(opt.value)}
                className={`w-full py-3.5 rounded-2xl border-2 text-[14px] transition-all ${selected ? opt.active : opt.color} hover:-translate-y-0.5 cursor-pointer`}
                style={{ fontWeight: selected ? 700 : 500 }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-all text-[13px] cursor-pointer"
            style={{ fontWeight: 500 }}
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={onNext}
            disabled={answer === null}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[13px] transition-all ${
              answer !== null
                ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-md hover:-translate-y-0.5 cursor-pointer"
                : "bg-slate-100 text-slate-400 cursor-not-allowed"
            }`}
            style={{ fontWeight: 700 }}
          >
            {index === total - 1 ? "See Results" : "Next"}{" "}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
